import { parseISO } from "date-fns";
import { capitalizeAndReplace } from "@/lib/utils";

export type InstallationHealthStatus = "red" | "yellow" | "green" | "black"


export type InstallationData = {
  name: string;
  equipment: number;
  asset: AssetData;
  asset_manual?: string;
  id: number;
  sensor_kit?: string;
  sensor_kit_periods: SensorKitPeriod[];
  installation_date?: string;
  serial_number?: string;
  sublocation: string;
  sublocation_id: number;
  location: {
    id: number;
    name: string;
    organization_id: number;
  }
  images: InstallationImage[],
  status: string;
  health_status: InstallationHealthStatus
  position?: GridPosition;
  sort_order: number;
  expected_voltage_per_phase?: string;
  phase_system_type?: string;
  equipment_model?: {
    refrigerant_type?: string;
    phase_count?: number | null;
  };
}

export const hasPosition = (installation: InstallationData): installation is InstallationData & {
  position: GridPosition
} => installation.position !== undefined;

export type GridPosition = {
  grid_x: number;
  grid_y: number;
}

type InstallationImage = {
  image_url: string
  title: string
  description: string
  image_filename: string
  primary: boolean
  category: string | null
}

export type CurrentClampMapping = {
  placements: { clamp_input: string; electrical_line: string; scope: "component" | "equipment" }[];
  measures_equipment_power_directly: boolean;
  component_power_phase_coverage: "three_phase" | "partial" | "none";
};

export type SensorKitPeriod = {
  start: string;
  stop?: string;
  sensor_kit: {
    id: number;
    serial_number: string;
    config: {
      model: "combustion" | "point_capture" | "direct_air_capture" | "refrigerant";
      version: string;
    }
  };
  current_clamp_mapping?: CurrentClampMapping | null;
}

export type AssetNameplateCapacityUnit = "refrigeration_ton" | "MBH";

export type AssetNameplateCapacity = {
  magnitude: number;
  units: AssetNameplateCapacityUnit
}

export type AssetData = {
  asset_subtype?: string;
  asset_type: string;
  description: string;
  fuel_type: string;
  id: number;
  make: string;
  model: string;
  name: string;
  nameplate_capacity?: AssetNameplateCapacity;
  refrigerant_type?: string;
}

const getSortedSensorKitPeriods = (asset: InstallationData) =>
  [...asset.sensor_kit_periods].sort((a, b) => {
    // Compare start dates
    const startComparison = parseISO(a.start).getTime() - parseISO(b.start).getTime();
    if (startComparison !== 0) return startComparison;

    // If start dates are equal, prioritize active periods (no stop date)
    if (a.stop === undefined && b.stop !== undefined) return 1;
    if (a.stop !== undefined && b.stop === undefined) return -1;

    // If both have stop dates, compare them
    if (a.stop && b.stop) {
      return parseISO(a.stop).getTime() - parseISO(b.stop).getTime();
    }

    return 0;
  });

export const getEarliestSensorKitPeriod = (asset: InstallationData): SensorKitPeriod | undefined => getSortedSensorKitPeriods(asset)[0]

export const getLatestSensorKitPeriod = (asset: InstallationData): SensorKitPeriod | undefined => {
  // First, try to find an active period (no stop date)
  const activePeriod = asset.sensor_kit_periods.find(period => period.stop === undefined);
  if (activePeriod) return activePeriod;

  // If no active period, get the one with the latest start date
  const sortedPeriods = getSortedSensorKitPeriods(asset);
  return sortedPeriods.at(-1);
};

export const isRefrigerant = (asset: InstallationData): boolean => {
  const latestSensorKitPeriod = getLatestSensorKitPeriod(asset);
  const hasRefrigerantSK = !!latestSensorKitPeriod && latestSensorKitPeriod.sensor_kit.config.model === "refrigerant";
  const isRefrigerantEquipment = asset.asset.asset_type === "air_conditioner"
  return hasRefrigerantSK || isRefrigerantEquipment
}

export const isCapture = (asset: InstallationData): boolean => {
  const latestSensorKitPeriod = getLatestSensorKitPeriod(asset);
  return !!latestSensorKitPeriod && latestSensorKitPeriod.sensor_kit.config.model === "direct_air_capture";
}

/**
 * True if this installation's active sensor kit period has equipment-scoped clamps —
 * meaning the clamps measure the whole equipment's electrical feed, not just this component.
 *
 * When true, per-component power and voltage are unreliable (the current is measuring a
 * combined load, so the firmware's phase-angle computation is invalid and the voltage
 * reading is not representative of the component in isolation). Only current is valid.
 *
 * Returns false when there is no clamp mapping configured — null mapping means
 * unconfigured, not absent, so we default to showing all charts.
 */
export const installationHasDirectEquipmentPowerMonitoring = (installation: InstallationData): boolean => {
  const period = getLatestSensorKitPeriod(installation);
  return !!period?.current_clamp_mapping?.measures_equipment_power_directly;
};

/** True if any installation in the list has direct equipment power monitoring. */
export const hasDirectEquipmentPowerMonitoring = (installations: InstallationData[]): boolean =>
  installations.some(installationHasDirectEquipmentPowerMonitoring);

export const numDataPointsPerMinutePerAsset = (asset: InstallationData): number => {
  if (isRefrigerant(asset)) {
    return 3000 // ~ ((10 dp/s * 5 TCs) + (0.1 dp/s * (3 current + 3 voltage)) * 60 sec/min
  } else {
    return 480
  }
}

export const numDataPointsPerMinuteForAssets = (assets: InstallationData[]): number => {
  return assets.map(asset => numDataPointsPerMinutePerAsset(asset)).reduce((a, b) => a + b, 0)
}

export const getShortEquipmentDetails = (equipment: AssetData): string => {

  if (!equipment.nameplate_capacity) {
    return `${equipment.make} ${capitalizeAndReplace(equipment.asset_subtype || equipment.asset_type)}`
  }

  const shortSingularUnit: Record<AssetNameplateCapacityUnit, string> = {
    "refrigeration_ton": "ton",
    "MBH": "MBH"
  }

  return `${equipment.make} ${equipment.nameplate_capacity.magnitude}-${shortSingularUnit[equipment.nameplate_capacity.units]} ${capitalizeAndReplace(equipment.asset_subtype || equipment.asset_type)}`
}

export const getPrimaryImage = (asset: InstallationData): InstallationImage | undefined => asset.images.find(image => image.primary);

export const installationHealthStatusBorderClass: Record<InstallationHealthStatus, string> = {
  "red": "border-red-500",
  "yellow": "border-yellow-500",
  "green": "border-green-500",
  "black": "border-grey-700" //off-black (black seems harsh)

}

export const installationHealthStatusFillClass: Record<InstallationHealthStatus, string> = {
  "red": "fill-red-500",
  "yellow": "fill-yellow-500",
  "green": "fill-green-500",
  "black": "fill-grey-700"
}

export const installationHealthStatusBackgroundClass: Record<InstallationHealthStatus, string> = {
  "red": "bg-red-500",
  "yellow": "bg-yellow-500",
  "green": "bg-green-500",
  "black": "bg-grey-700"
}

export const installationHealthStatusTextClassSeverityMessage: Record<InstallationHealthStatus, string> = {
  "red": "Service is recommended immediately",
  "yellow": "Service is recommended at your earliest convenience",
  "green": "No issues",
  "black": "No issues"
}
